// Feature enums for type safety
export enum Feature {
  DASHBOARD = 'Dashboard',
  TRIP = 'Trip',
  SCHEDULE_TRIP = 'ScheduleTrip',
  TRACKING = 'Tracking',
  REPORT = 'Report',
  VENDOR_MANAGEMENT = 'VenderManagement',
  VEHICLE_MANAGEMENT = 'VehicleManagement',
  DRIVER_MANAGEMENT = 'DriverManagement',
  ROUTE_MANAGEMENT = 'RouteManagement',
  RATE_CARD_MANAGEMENT = 'RateCardManagement',
  BILLING_MANAGEMENT = 'BillingManagement',
  TRANSIT_MANAGEMENT = 'TransitManagement',
  CITY_RESTRICTION = 'CityRestriction',
  USER_MANAGEMENT = 'UserManagement'
}

// Component enums for type safety
export enum Component {
  DASHBOARD = 'DashboardComponent',
  TRIP_MANAGEMENT = 'TripManagementComponent',
  CREATE_TRIP = 'CreateTripComponent',
  SCHEDULE_TRIP = 'ScheduleTripComponent',
  TRACKING = 'TrackingComponent',
  TRIP_TRACK = 'TripTrackComponent',
  VEHICLE_INFO = 'VehicleInfoComponent',
  REPORT_ANALYTICS = 'ReportAnalyticsComponent',
  VENDOR_MANAGEMENT = 'VenderManagementComponent',
  VENDOR_ONBOARD = 'VendorOnboardComponent',
  VEHICLE_MANAGEMENT = 'VehicleManagementComponent',
  VEHICLE_ONBOARD = 'VehicleOnboardComponent',
  DRIVER_MANAGEMENT = 'DriverManagementComponent',
  DRIVER_ONBOARD = 'DriverOnboardComponent',
  ROUTE_MANAGEMENT = 'RouteManagementComponent',
  CREATE_ROUTE = 'CreateRouteComponent',
  RATE_CARD_MANAGEMENT = 'RateCardManagementComponent',
  BILLING_MANAGEMENT = 'BillingManagementComponent',
  TRIP_SUMMARY = 'TripSummaryComponent',
  BILL_GENERATION = 'BillGenerationComponent',
  GENERATED_BILL = 'GeneratedBillComponent',
  TRANSIT_MANAGEMENT = 'TransitManagementComponent',
  TRANSIT_ONBOARD = 'TransitOnboardComponent',
  CITY_RESTRICTION = 'CityRestrictionComponent',
  USER_MANAGEMENT = 'UserManagementComponent',
  USER_ONBOARD = 'UserOnboardComponent',
  INCIDENT_REPORT_DIALOG = 'IncidentReportDialogComponent',
  ROUTE_DETAILS_DIALOG = 'RouteDetailsDialogComponent',
  EDIT_ROUTE_TIMELINE_DIALOG = 'EditRouteTimelineDialogComponent'
}

// Menu item types
export type MenuItemType = 'single' | 'group';

// Menu item interface
export interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  route?: string;
  feature: Feature;
  type: MenuItemType;
  children?: MenuItem[];
}

// Feature detail interface
export interface FeatureDetail {
  name: string;
  component: Component;
  path: string;
  feature: Feature;
  accessNote?: string;
}

// Component to feature mapping
export const COMPONENT_TO_FEATURE_MAP: Record<Component, Feature> = {
  [Component.DASHBOARD]: Feature.DASHBOARD,
  [Component.TRIP_MANAGEMENT]: Feature.TRIP,
  [Component.CREATE_TRIP]: Feature.TRIP,
  [Component.SCHEDULE_TRIP]: Feature.SCHEDULE_TRIP,
  [Component.TRACKING]: Feature.TRACKING,
  [Component.TRIP_TRACK]: Feature.TRACKING,
  [Component.VEHICLE_INFO]: Feature.TRACKING,
  [Component.REPORT_ANALYTICS]: Feature.REPORT,
  [Component.VENDOR_MANAGEMENT]: Feature.VENDOR_MANAGEMENT,
  [Component.VENDOR_ONBOARD]: Feature.VENDOR_MANAGEMENT,
  [Component.VEHICLE_MANAGEMENT]: Feature.VEHICLE_MANAGEMENT,
  [Component.VEHICLE_ONBOARD]: Feature.VEHICLE_MANAGEMENT,
  [Component.DRIVER_MANAGEMENT]: Feature.DRIVER_MANAGEMENT,
  [Component.DRIVER_ONBOARD]: Feature.DRIVER_MANAGEMENT,
  [Component.ROUTE_MANAGEMENT]: Feature.ROUTE_MANAGEMENT,
  [Component.CREATE_ROUTE]: Feature.ROUTE_MANAGEMENT,
  [Component.RATE_CARD_MANAGEMENT]: Feature.RATE_CARD_MANAGEMENT,
  [Component.BILLING_MANAGEMENT]: Feature.BILLING_MANAGEMENT,
  [Component.TRIP_SUMMARY]: Feature.BILLING_MANAGEMENT,
  [Component.BILL_GENERATION]: Feature.BILLING_MANAGEMENT,
  [Component.GENERATED_BILL]: Feature.BILLING_MANAGEMENT,
  [Component.TRANSIT_MANAGEMENT]: Feature.TRANSIT_MANAGEMENT,
  [Component.TRANSIT_ONBOARD]: Feature.TRANSIT_MANAGEMENT,
  [Component.CITY_RESTRICTION]: Feature.CITY_RESTRICTION,
  [Component.USER_MANAGEMENT]: Feature.USER_MANAGEMENT,
  [Component.USER_ONBOARD]: Feature.USER_MANAGEMENT,
  [Component.INCIDENT_REPORT_DIALOG]: Feature.TRIP,
  [Component.ROUTE_DETAILS_DIALOG]: Feature.ROUTE_MANAGEMENT,
  [Component.EDIT_ROUTE_TIMELINE_DIALOG]: Feature.ROUTE_MANAGEMENT
};

// Feature to feature mapping (identity mapping for consistency)
export const FEATURE_TO_FEATURE_MAP: Record<Feature, Feature> = {
  [Feature.DASHBOARD]: Feature.DASHBOARD,
  [Feature.TRIP]: Feature.TRIP,
  [Feature.SCHEDULE_TRIP]: Feature.SCHEDULE_TRIP,
  [Feature.TRACKING]: Feature.TRACKING,
  [Feature.REPORT]: Feature.REPORT,
  [Feature.VENDOR_MANAGEMENT]: Feature.VENDOR_MANAGEMENT,
  [Feature.VEHICLE_MANAGEMENT]: Feature.VEHICLE_MANAGEMENT,
  [Feature.DRIVER_MANAGEMENT]: Feature.DRIVER_MANAGEMENT,
  [Feature.ROUTE_MANAGEMENT]: Feature.ROUTE_MANAGEMENT,
  [Feature.RATE_CARD_MANAGEMENT]: Feature.RATE_CARD_MANAGEMENT,
  [Feature.BILLING_MANAGEMENT]: Feature.BILLING_MANAGEMENT,
  [Feature.TRANSIT_MANAGEMENT]: Feature.TRANSIT_MANAGEMENT,
  [Feature.CITY_RESTRICTION]: Feature.CITY_RESTRICTION,
  [Feature.USER_MANAGEMENT]: Feature.USER_MANAGEMENT
};

// Menu configuration
export const MENU_CONFIGURATION: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    feature: Feature.DASHBOARD,
    type: 'single'
  },
  {
    id: 'tracking',
    label: 'Tracking',
    icon: 'location_on',
    feature: Feature.TRACKING,
    type: 'single'
  },
  {
    id: 'tripManagement',
    label: 'Trip Management',
    icon: 'directions_bus',
    feature: Feature.TRIP,
    type: 'group',
    children: [
      {
        id: 'trips',
        label: 'Trips',
        feature: Feature.TRIP,
        type: 'single'
      },
      {
        id: 'scheduleTrips',
        label: 'Schedule Trips',
        feature: Feature.SCHEDULE_TRIP,
        type: 'single'
      }
    ]
  },
  {
    id: 'reports',
    label: 'Reports & Analytics',
    icon: 'insert_chart',
    feature: Feature.REPORT,
    type: 'group',
    children: [
      {
        id: 'tripReports',
        label: 'Trip Reports',
        feature: Feature.REPORT,
        type: 'single'
      }
    ]
  },
  {
    id: 'routeManagement',
    label: 'Route Management',
    icon: 'alt_route',
    feature: Feature.ROUTE_MANAGEMENT,
    type: 'single'
  },
  {
    id: 'vendorManagement',
    label: 'Vendor Management',
    icon: 'store',
    feature: Feature.VENDOR_MANAGEMENT,
    type: 'single'
  },
  {
    id: 'vehicleManagement',
    label: 'Vehicle Management',
    icon: 'local_shipping',
    feature: Feature.VEHICLE_MANAGEMENT,
    type: 'single'
  },
  {
    id: 'driverManagement',
    label: 'Driver Management',
    icon: 'person',
    feature: Feature.DRIVER_MANAGEMENT,
    type: 'single'
  },
  {
    id: 'rateCardManagement',
    label: 'Rate Card Management',
    icon: 'credit_card',
    feature: Feature.RATE_CARD_MANAGEMENT,
    type: 'single'
  },
  {
    id: 'billingManagement',
    label: 'Billing Management',
    icon: 'receipt',
    feature: Feature.BILLING_MANAGEMENT,
    type: 'single'
  },
  {
    id: 'transitManagement',
    label: 'Transit',
    icon: 'local_shipping',
    feature: Feature.TRANSIT_MANAGEMENT,
    type: 'group',
    children: [
      {
        id: 'transit',
        label: 'Transit',
        feature: Feature.TRANSIT_MANAGEMENT,
        type: 'single'
      },
      {
        id: 'cityRestriction',
        label: 'City Restriction',
        feature: Feature.CITY_RESTRICTION,
        type: 'single'
      }
    ]
  },
  {
    id: 'userManagement',
    label: 'User Management',
    icon: 'people',
    feature: Feature.USER_MANAGEMENT,
    type: 'single'
  }
];

// Feature details array
export const FEATURE_DETAILS: FeatureDetail[] = [
  {
    name: 'Dashboard',
    component: Component.DASHBOARD,
    path: 'dashboard',
    feature: Feature.DASHBOARD
  },
  {
    name: 'Trip Management',
    component: Component.TRIP_MANAGEMENT,
    path: 'tripManagement',
    feature: Feature.TRIP
  },
  {
    name: 'Create Trip',
    component: Component.CREATE_TRIP,
    path: 'tripManagement/create',
    feature: Feature.TRIP
  },
  {
    name: 'Edit Trip',
    component: Component.CREATE_TRIP,
    path: 'tripManagement/edit/:id',
    feature: Feature.TRIP
  },
  {
    name: 'Schedule Trip',
    component: Component.SCHEDULE_TRIP,
    path: 'tripManagement/schedule',
    feature: Feature.SCHEDULE_TRIP
  },
  {
    name: 'Create Scheduled Trip',
    component: Component.CREATE_TRIP,
    path: 'tripManagement/schedule/create',
    feature: Feature.TRIP
  },
  {
    name: 'Edit Scheduled Trip',
    component: Component.CREATE_TRIP,
    path: 'tripManagement/schedule/edit/:id',
    feature: Feature.TRIP
  },
  {
    name: 'Vendor Management',
    component: Component.VENDOR_MANAGEMENT,
    path: 'VenderManagement',
    feature: Feature.VENDOR_MANAGEMENT
  },
  {
    name: 'Vendor Onboard',
    component: Component.VENDOR_ONBOARD,
    path: 'VenderManagement/onboard',
    feature: Feature.VENDOR_MANAGEMENT
  },
  {
    name: 'Edit Vendor',
    component: Component.VENDOR_ONBOARD,
    path: 'VenderManagement/onboard/:id',
    feature: Feature.VENDOR_MANAGEMENT
  },
  {
    name: 'Vehicle Management',
    component: Component.VEHICLE_MANAGEMENT,
    path: 'VehicleManagement',
    feature: Feature.VEHICLE_MANAGEMENT
  },
  {
    name: 'Vehicle Onboard',
    component: Component.VEHICLE_ONBOARD,
    path: 'VehicleManagement/onboard',
    feature: Feature.VEHICLE_MANAGEMENT
  },
  {
    name: 'Edit Vehicle',
    component: Component.VEHICLE_ONBOARD,
    path: 'VehicleManagement/onboard/:id',
    feature: Feature.VEHICLE_MANAGEMENT
  },
  {
    name: 'Driver Management',
    component: Component.DRIVER_MANAGEMENT,
    path: 'DriverManagement',
    feature: Feature.DRIVER_MANAGEMENT
  },
  {
    name: 'Driver Onboard',
    component: Component.DRIVER_ONBOARD,
    path: 'DriverManagement/onboard',
    feature: Feature.DRIVER_MANAGEMENT
  },
  {
    name: 'Edit Driver',
    component: Component.DRIVER_ONBOARD,
    path: 'DriverManagement/onboard/:id',
    feature: Feature.DRIVER_MANAGEMENT
  },
  {
    name: 'Route Management',
    component: Component.ROUTE_MANAGEMENT,
    path: 'RouteManagement',
    feature: Feature.ROUTE_MANAGEMENT
  },
  {
    name: 'Create Route',
    component: Component.CREATE_ROUTE,
    path: 'RouteManagement/create',
    feature: Feature.ROUTE_MANAGEMENT
  },
  {
    name: 'Edit Route',
    component: Component.CREATE_ROUTE,
    path: 'RouteManagement/edit/:id',
    feature: Feature.ROUTE_MANAGEMENT
  },
  {
    name: 'Rate Card Management',
    component: Component.RATE_CARD_MANAGEMENT,
    path: 'RateCardManagement',
    feature: Feature.RATE_CARD_MANAGEMENT
  },
  {
    name: 'Report Analytics',
    component: Component.REPORT_ANALYTICS,
    path: 'Report/trip-report',
    feature: Feature.REPORT
  },
  {
    name: 'Billing Management',
    component: Component.BILLING_MANAGEMENT,
    path: 'BillingManagement',
    feature: Feature.BILLING_MANAGEMENT
  },
  {
    name: 'Trip Summary',
    component: Component.TRIP_SUMMARY,
    path: 'BillingManagement/trip-summary',
    feature: Feature.BILLING_MANAGEMENT
  },
  {
    name: 'Bill Generation',
    component: Component.BILL_GENERATION,
    path: 'BillingManagement/bill-generation',
    feature: Feature.BILLING_MANAGEMENT
  },
  {
    name: 'Generated Bill',
    component: Component.GENERATED_BILL,
    path: 'BillingManagement/generated-bill',
    feature: Feature.BILLING_MANAGEMENT
  },
  {
    name: 'User Management',
    component: Component.USER_MANAGEMENT,
    path: 'UserManagement',
    feature: Feature.USER_MANAGEMENT,
    accessNote: 'Available to admin users by default'
  },
  {
    name: 'User Onboard',
    component: Component.USER_ONBOARD,
    path: 'UserManagement/onboard',
    feature: Feature.USER_MANAGEMENT,
    accessNote: 'Available to admin users by default'
  },
  {
    name: 'Edit User',
    component: Component.USER_ONBOARD,
    path: 'UserManagement/onboard/:id',
    feature: Feature.USER_MANAGEMENT,
    accessNote: 'Available to admin users by default'
  },
  {
    name: 'Transit Management',
    component: Component.TRANSIT_MANAGEMENT,
    path: 'transitManagement',
    feature: Feature.TRANSIT_MANAGEMENT
  },
  {
    name: 'Transit Onboard',
    component: Component.TRANSIT_ONBOARD,
    path: 'transitManagement/onboard',
    feature: Feature.TRANSIT_MANAGEMENT
  },
  {
    name: 'Edit Transit',
    component: Component.TRANSIT_ONBOARD,
    path: 'transitManagement/edit/:id',
    feature: Feature.TRANSIT_MANAGEMENT
  },
  {
    name: 'City Restriction',
    component: Component.CITY_RESTRICTION,
    path: 'transitManagement/city-restriction',
    feature: Feature.CITY_RESTRICTION
  },
  {
    name: 'Tracking',
    component: Component.TRACKING,
    path: 'tracking',
    feature: Feature.TRACKING
  },
  {
    name: 'Trip Track',
    component: Component.TRIP_TRACK,
    path: 'tracking/trip-track',
    feature: Feature.TRACKING
  },
  {
    name: 'Vehicle Info',
    component: Component.VEHICLE_INFO,
    path: 'tracking/vehicle-info',
    feature: Feature.TRACKING
  },
  {
    name: 'Route Details Dialog',
    component: Component.ROUTE_DETAILS_DIALOG,
    path: 'route-details-dialog',
    feature: Feature.ROUTE_MANAGEMENT
  },
  {
    name: 'Edit Route Timeline Dialog',
    component: Component.EDIT_ROUTE_TIMELINE_DIALOG,
    path: 'edit-route-timeline-dialog',
    feature: Feature.ROUTE_MANAGEMENT
  },
  {
    name: 'Incident Report Dialog',
    component: Component.INCIDENT_REPORT_DIALOG,
    path: 'incident-report-dialog',
    feature: Feature.TRIP
  }
];

// Complete feature configuration object
export const FEATURE_CONFIG = {
  menuConfiguration: {
    mainMenuItems: MENU_CONFIGURATION
  },
  componentToFeatureMapping: COMPONENT_TO_FEATURE_MAP,
  featureToFeatureMapping: FEATURE_TO_FEATURE_MAP,
  features: FEATURE_DETAILS
};

// Helper functions
export function getFeatureByComponent(component: Component): Feature {
  return COMPONENT_TO_FEATURE_MAP[component];
}

export function getComponentByFeature(feature: Feature): Component[] {
  return FEATURE_DETAILS
    .filter(detail => detail.feature === feature)
    .map(detail => detail.component);
}

export function getFeatureDetails(feature: Feature): FeatureDetail[] {
  return FEATURE_DETAILS.filter(detail => detail.feature === feature);
}

export function getFeatureByPath(path: string): Feature | undefined {
  const detail = FEATURE_DETAILS.find(detail => detail.path === path);
  return detail?.feature;
}
